import isNullOrUndefined from "_shared/_utilities/is-null-or-undefined";
import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import AHDate from "_shared/dates/date";
import Guid from "_shared/guids/guid";
import Topic from "messaging/forums/topics/topic";
import ForumEntityClassSchema from "notifications/message-notifications/forum-info/forum-entity-class-schema";
import { z } from "zod";

export type NetworkConnectionForumJSON = z.infer<typeof NetworkConnectionForumSchema.NetworkConnectionForumJSONZ>;
export type NetworkConnectionForumParams = z.infer<typeof NetworkConnectionForumSchema.NetworkConnectionForumZ>;

export default class NetworkConnectionForumSchema {
  public static readonly NetworkConnectionForumJSONZ = z.object({
    id: z.uuid().nullish(),
    name: z.string(),
    createdDate: z.iso.datetime({ offset: true }).nullish(),
    entityClass: z.string(),
    entityId: z.uuid().nullish(),
    subscriberIds: z.array(z.uuid()).nullish(),
    context: z.string().nullish(),
  });

  public static readonly NetworkConnectionForumZ = z.object({
    id: z.instanceof(Guid).optional(),
    name: z.string(),
    createdDate: z.instanceof(AHDate).optional(),
    topic: z.instanceof(Topic).optional(),
    subscriberIds: z.array(z.instanceof(Guid)).optional(),
  });

  public static validate(values: unknown): NetworkConnectionForumParams {
    return parseOrThrow(NetworkConnectionForumSchema.NetworkConnectionForumZ, values, {
      payloadSample: values,
      schemaName: "NetworkConnectionForumSchema",
      operation: "validate",
      logError: true,
    });
  }

  public static validateJSON(json: unknown): NetworkConnectionForumJSON {
    return parseOrThrow(NetworkConnectionForumSchema.NetworkConnectionForumJSONZ, json, {
      payloadSample: json,
      schemaName: "NetworkConnectionForumSchema",
      operation: "validateJSON",
      logError: true,
    });
  }

  public static validateJSONArray(jsonArray: unknown[]): NetworkConnectionForumJSON[] {
    return jsonArray.map((json) => NetworkConnectionForumSchema.validateJSON(json));
  }

  public static isValid(values: unknown): values is NetworkConnectionForumParams {
    try {
      NetworkConnectionForumSchema.validate(values);
      return true;
    } catch {
      return false;
    }
  }

  public static isValidJSON(json: unknown): json is NetworkConnectionForumJSON {
    try {
      NetworkConnectionForumSchema.validateJSON(json);
      return true;
    } catch {
      return false;
    }
  }

  public static deserialize(json: NetworkConnectionForumJSON): NetworkConnectionForumParams {
    if (isNullOrUndefined(json)) return json;
    const parsed = parseOrThrow(NetworkConnectionForumSchema.NetworkConnectionForumJSONZ, json, {
      payloadSample: json,
      schemaName: "NetworkConnectionForumSchema",
      operation: "deserialize",
      logError: true,
    });
    return NetworkConnectionForumSchema.codec.decode(parsed);
  }

  public static serialize(params: NetworkConnectionForumParams): NetworkConnectionForumJSON {
    if (isNullOrUndefined(params)) return params;
    const parsed = NetworkConnectionForumSchema.validate(params);
    if (isNullOrUndefined(parsed)) return parsed;
    return NetworkConnectionForumSchema.codec.encode(parsed);
  }

  private static readonly codec = z.codec(
    NetworkConnectionForumSchema.NetworkConnectionForumJSONZ,
    NetworkConnectionForumSchema.NetworkConnectionForumZ,
    {
      decode: (data) => ({
        id: Guid.fromJSON(data.id) ?? undefined,
        name: data.name,
        createdDate: AHDate.fromJSON(data.createdDate) ?? undefined,
        topic: Topic.fromJSON(data) ?? undefined,
        subscriberIds:
          data.subscriberIds
            ?.map((subscriberId) => Guid.fromJSON(subscriberId))
            .filter((id): id is Guid => id !== undefined) ?? [],
      }),
      encode: (forum) => ({
        id: forum.id?.toJSON(),
        name: forum.name ?? "",
        createdDate: forum.createdDate ? forum.createdDate.toJSON() : undefined,
        entityClass: ForumEntityClassSchema.validateJSON(forum.topic?.entityClass?.toJSON()),
        entityId: forum.topic?.entityId?.toJSON(),
        context: forum.topic?.context ?? "",
        subscriberIds: forum.subscriberIds?.map((subscriberId) => subscriberId.toJSON()) ?? [],
      }),
    }
  );
}
